import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Inquiry = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    email: '',
    preferred_location: '',
    room_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties?per_page=100`);
      setProperties(response.data.properties || []);
      
      // Extract unique room types from all properties
      const allRoomTypes = new Set();
      response.data.properties?.forEach(property => {
        // Check pricing to see which room types are available
        if (property.pricing?.single > 0) allRoomTypes.add('Single Sharing');
        if (property.pricing?.double > 0) allRoomTypes.add('Double Sharing');
        if (property.pricing?.triple > 0) allRoomTypes.add('Triple Sharing');
        if (property.pricing?.quadruple > 0) allRoomTypes.add('4-Sharing');
      });
      setRoomTypes([...allRoomTypes, 'Any']);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, formData);
      setSuccess(true);
      setFormData({
        full_name: '',
        mobile_number: '',
        email: '',
        preferred_location: '',
        room_type: '',
        message: ''
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError('Failed to submit inquiry. Please try again.');
      console.error('Error submitting inquiry:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
              Get in <span className="text-brand-purple">Touch</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </motion.div>

          {success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-xl mb-8 flex items-center gap-3"
              data-testid="inquiry-success-message"
            >
              <CheckCircle2 className="text-green-600" size={24} />
              <p className="font-body">Thank you! Your inquiry has been submitted successfully. We'll contact you soon.</p>
            </motion.div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-xl mb-8" data-testid="inquiry-error-message">
              <p className="font-body">{error}</p>
            </div>
          )}

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-gray-100"
            data-testid="inquiry-form"
          >
            <div className="space-y-6">
              <div>
                <Label htmlFor="full_name" className="font-body font-medium">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className="mt-2 font-body"
                  placeholder="Enter your full name"
                  data-testid="inquiry-full-name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="mobile_number" className="font-body font-medium">Mobile Number *</Label>
                  <Input
                    id="mobile_number"
                    name="mobile_number"
                    type="tel"
                    required
                    value={formData.mobile_number}
                    onChange={handleChange}
                    className="mt-2 font-body"
                    placeholder="+91 98765 43210"
                    data-testid="inquiry-mobile"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-body font-medium">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 font-body"
                    placeholder="your@email.com"
                    data-testid="inquiry-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="preferred_location" className="font-body font-medium">Preferred Location *</Label>
                  <Select
                    required
                    value={formData.preferred_location}
                    onValueChange={(value) => handleSelectChange('preferred_location', value)}
                  >
                    <SelectTrigger className="mt-2 font-body" data-testid="inquiry-location">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent data-testid="inquiry-location-content">
                      {properties.length > 0 ? (
                        properties.map((property) => (
                          <SelectItem 
                            key={property.id} 
                            value={property.name}
                            data-testid={`location-${property.id}`}
                          >
                            {property.name} - {property.area}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="any">Any Location</SelectItem>
                      )}
                      <SelectItem value="other" data-testid="location-other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="room_type" className="font-body font-medium">Room Type *</Label>
                  <Select
                    required
                    value={formData.room_type}
                    onValueChange={(value) => handleSelectChange('room_type', value)}
                  >
                    <SelectTrigger className="mt-2 font-body" data-testid="inquiry-room-type">
                      <SelectValue placeholder="Select room type" />
                    </SelectTrigger>
                    <SelectContent data-testid="inquiry-room-type-content">
                      {roomTypes.length > 0 ? (
                        roomTypes.map((type, index) => (
                          <SelectItem 
                            key={index} 
                            value={type.toLowerCase().replace(/\s+/g, '-')}
                            data-testid={`room-type-${index}`}
                          >
                            {type}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="single">Single Sharing</SelectItem>
                          <SelectItem value="double">Double Sharing</SelectItem>
                          <SelectItem value="triple">Triple Sharing</SelectItem>
                          <SelectItem value="quadruple">4-Sharing</SelectItem>
                          <SelectItem value="any">Any</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="font-body font-medium">Message (Optional)</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-2 font-body"
                  placeholder="Any specific requirements or questions..."
                  rows={5}
                  data-testid="inquiry-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-purple text-white py-4 rounded-full font-body text-lg font-medium hover:bg-opacity-90 transition-all shadow-lg shadow-purple-900/20 hover:shadow-xl hover:shadow-purple-900/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="inquiry-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Submitting...
                  </>
                ) : (
                  'Submit Inquiry'
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
};
